import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeft, UserPlus, UserMinus, Play, Users, Music, Share2 } from 'lucide-react';
import { followApi, audioApi, comboApi, getAudioFileUrl } from '@/lib/api';
import { AUDIO_COLORS, LANGUAGE_FLAGS } from '@/lib/constants';
import { AudioVisualBadge } from '@/components/audio/AudioVisuals';
import { getComboColorHex } from '@/lib/utils';
import { useAuth } from '@/contexts/AuthContext';
import { shareAudio } from '@/lib/share';

const UserProfilePage = () => {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [audios, setAudios] = useState([]);
  const [combos, setCombos] = useState([]);
  const [isFollowing, setIsFollowing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [followLoading, setFollowLoading] = useState(false);

  useEffect(() => {
    loadProfile();
  }, [userId]);

  const loadProfile = async () => {
    try {
      const [profileData, userAudios] = await Promise.all([
        followApi.getProfile(userId),
        audioApi.getFeed({ user_id: userId, limit: 20 })
      ]);
      
      setProfile(profileData);
      // Filtrar y ordenar por fecha (más recientes primero)
      const filtered = userAudios.filter(a => a.user_id === userId);
      filtered.sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
      setAudios(filtered);

      // Combos del usuario (públicos si no es el propio perfil)
      try {
        const userCombos = await comboApi.getUserCombos(userId);
        setCombos(userCombos);
      } catch (err) {
        console.error('Error cargando combos:', err);
      }
      
      // Verificar si seguimos a este usuario
      if (isAuthenticated && currentUser?.user_id !== userId) {
        const followStatus = await followApi.isFollowing(userId);
        setIsFollowing(followStatus.is_following);
      }
      
      setLoading(false);
    } catch (error) {
      console.error('Error loading profile:', error);
      setLoading(false);
    }
  };

  const handleToggleFollow = async () => {
    if (!isAuthenticated) {
      alert('Inicia sesión para seguir usuarios');
      return;
    }
    
    setFollowLoading(true);
    try {
      const result = await followApi.toggle(userId);
      setIsFollowing(result.is_following);
      // Actualizar contador
      setProfile(prev => ({
        ...prev,
        followers_count: prev.followers_count + (result.is_following ? 1 : -1)
      }));
    } catch (error) {
      console.error('Error toggling follow:', error);
    } finally {
      setFollowLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center">
        <div className="animate-spin w-10 h-10 border-4 border-purple-400 border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!profile) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 p-4">
        <button onClick={() => navigate(-1)} className="text-white/70 flex items-center gap-2 mb-6">
          <ArrowLeft size={20} /> Volver
        </button>
        <div className="text-center py-12">
          <p className="text-white/60">Usuario no encontrado</p>
        </div>
      </div>
    );
  }

  const isOwnProfile = currentUser?.user_id === userId;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/5 border-b border-white/10 p-4">
        <div className="max-w-lg mx-auto flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-white/70">
            <ArrowLeft size={24} />
          </button>
          <h1 className="text-xl font-bold text-white flex-1">Perfil</h1>
        </div>
      </header>

      <main className="max-w-lg mx-auto px-4 py-6">
        {/* Profile Card */}
        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-6 text-center border border-white/10 mb-6">
          {/* Avatar */}
          {profile.picture ? (
            <img 
              src={profile.picture} 
              alt={profile.name}
              className="w-24 h-24 rounded-full mx-auto mb-4 border-4 border-white/20"
            />
          ) : (
            <div className="w-24 h-24 rounded-full mx-auto mb-4 bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-4xl text-white font-bold">
              {profile.name?.charAt(0)?.toUpperCase()}
            </div>
          )}
          
          <h2 className="text-2xl font-bold text-white mb-1">
            {profile.name}
            {profile.is_official && (
              <span className="bg-purple-500 text-white text-[10px] px-2 py-0.5 rounded-full font-bold ml-2">
                ✓ Verificado
              </span>
            )}
          </h2>
          {profile.city && (
            <p className="text-white/50 text-sm mb-4">📍 {profile.city}, {profile.country}</p>
          )}
          
          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-white/5 rounded-xl p-3">
              <p className="text-2xl font-bold text-white">{profile.audios_count}</p>
              <p className="text-white/50 text-xs flex items-center justify-center gap-1">
                <Music size={12} /> Audios
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <p className="text-2xl font-bold text-white">{profile.followers_count}</p>
              <p className="text-white/50 text-xs flex items-center justify-center gap-1">
                <Users size={12} /> Seguidores
              </p>
            </div>
            <div className="bg-white/5 rounded-xl p-3">
              <p className="text-2xl font-bold text-white">{profile.following_count}</p>
              <p className="text-white/50 text-xs">Siguiendo</p>
            </div>
          </div>
          
          {/* Follow Button */}
          {!isOwnProfile && (
            <button
              onClick={handleToggleFollow}
              disabled={followLoading}
              className={`w-full py-3 rounded-xl font-bold flex items-center justify-center gap-2 transition-all ${
                isFollowing 
                  ? 'bg-white/10 text-white border border-white/20' 
                  : 'bg-gradient-to-r from-purple-500 to-pink-500 text-white'
              }`}
            >
              {followLoading ? (
                <div className="animate-spin w-5 h-5 border-2 border-white border-t-transparent rounded-full" />
              ) : isFollowing ? (
                <>
                  <UserMinus size={20} />
                  Siguiendo
                </>
              ) : (
                <>
                  <UserPlus size={20} />
                  Seguir
                </>
              )}
            </button>
          )}
          
          {isOwnProfile && (
            <Link 
              to="/perfil"
              className="block w-full py-3 rounded-xl font-bold bg-white/10 text-white border border-white/20"
            >
              Editar mi perfil
            </Link>
          )}
        </div>

        {/* User's Audios */}
        <h3 className="text-white font-bold text-lg mb-3">🎙️ Audios de {profile.name?.split(' ')[0]}</h3>
        
        {audios.length === 0 ? (
          <div className="bg-white/10 rounded-2xl p-8 text-center">
            <p className="text-white/50">Aún no ha publicado audios</p>
          </div>
        ) : (
          <div className="space-y-2">
            {audios.map((audio) => {
              const colorData = AUDIO_COLORS[audio.color] || AUDIO_COLORS.blue;
              const langFlag = LANGUAGE_FLAGS[audio.language]?.flag || '🇪🇸';
              
              return (
                <div
                  key={audio.audio_id}
                  className="bg-white/5 hover:bg-white/10 rounded-xl p-3 flex items-center gap-3 transition-colors"
                >
                  <Link
                    to={`/audio/${audio.audio_id}`}
                    className="flex items-center gap-3 flex-1 min-w-0"
                  >
                    <AudioVisualBadge
                      shape={audio.shape}
                      color={audio.color}
                      emoji={audio.emoji || colorData.emoji}
                      language={audio.language}
                      size="sm"
                    />
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <p className="text-white font-medium text-sm">{audio.title || colorData.name}</p>
                        <span className="text-base">{langFlag}</span>
                      </div>
                      <p className="text-white/50 text-xs">{audio.duration_seconds}s</p>
                    </div>
                  </Link>
                  
                  <button
                    onClick={() => shareAudio(audio, window.location.origin)}
                    className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center hover:bg-white/20"
                  >
                    <Share2 size={16} className="text-white" />
                  </button>
                  
                  <Link
                    to={`/audio/${audio.audio_id}`}
                    className="w-10 h-10 rounded-full bg-white/20 flex items-center justify-center"
                  >
                    <Play size={18} className="text-white ml-0.5" />
                  </Link>
                </div>
              );
            })}
          </div>
        )}

        {/* Combos del usuario */}
        {combos.length > 0 && (
          <section className="mt-6">
            <h3 className="text-white font-bold text-lg mb-3">🎧 Combos de {profile.name?.split(' ')[0]}</h3>
            <div className="space-y-3">
              {combos.map((combo) => {
                const comboHex = getComboColorHex(combo);
                return (
                <Link
                  key={combo.combo_id}
                  to={`/combo/${combo.combo_id}`}
                  className="bg-white/5 hover:bg-white/10 rounded-xl p-4 border border-white/10 flex items-center gap-3 transition-colors"
                  style={comboHex ? { borderLeft: `4px solid ${comboHex}` } : undefined}
                >
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${comboHex ? '' : 'bg-gradient-to-br from-purple-500 to-pink-500'}`}
                    style={comboHex ? { backgroundColor: comboHex } : undefined}
                  >
                    <span className="text-xl">🎧</span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-white font-medium text-sm truncate">{combo.name}</p>
                    <p className="text-white/50 text-xs">
                      {combo.rules?.length > 0
                        ? `${combo.rules.length} temática${combo.rules.length !== 1 ? 's' : ''} (auto)`
                        : `${combo.audios?.length || 0} audios`}
                    </p>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${
                    combo.is_public ? 'bg-green-500/20 text-green-400' : 'bg-white/10 text-white/50'
                  }`}>
                    {combo.is_public ? 'Público' : 'Privado'}
                  </span>
                </Link>
                );
              })}
            </div>
          </section>
        )}
      </main>
    </div>
  );
};

export default UserProfilePage;
